import type { ColumnType } from '@byecode/ui'
import { Box, DesktopModal, Flex, IconFont, Select, Table, Text } from '@byecode/ui'
import type { FlowRunStatus } from '@lighthouse/shared'
import { TooltipText, useAtomData } from '@lighthouse/shared'
import { useStore } from '@xyflow/react'
import { lightFormat } from 'date-fns'
import { memo, useCallback, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { useImmer } from 'use-immer'

import { appEnvListAtom } from '@/atoms/application/state'
import { EnvTag } from '@/components/DataSourceList/Env/EnvTag'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import type { WorkflowRunLog } from '@/services/types'
import type { FlowLogsWithPaginationParams, FlowRunStatusTab, RunType } from '@/shared/reusable'
import { useFlowLogsWithPagination } from '@/shared/reusable'

import {
    FLOW_RUNNING_STATUS_OPTIONS,
    RUNNING_STATUS_COLOR_MAP,
    RUNNING_STATUS_ICON_MAP,
    RUNNING_STATUS_MAP,
    RUNNING_TYPE_MAP,
    RUNNING_TYPES_OPTIONS
} from '../../constants'

const SCxHeader = styled(Flex)`
    padding-top: 20px;
    padding-bottom: 12px;
`

interface FlowHistoryProps {
    isShowWorkflowName?: boolean
    workflowId?: string
    logId?: string
    onRowClick?: (val: WorkflowRunLog) => void
}

export const FlowHistory = memo(({ workflowId, logId, onRowClick }: FlowHistoryProps) => {
    const envList = useAtomData(appEnvListAtom)
    const [queryPayload, setQueryPayload] = useImmer<FlowLogsWithPaginationParams>({
        workflowId,
        status: 'all',
        runEnv: 'all',
        envId: 'all'
    })
    const { logs, total, setCurrentPage } = useFlowLogsWithPagination(queryPayload)
    const isAllWorkflow = useMemo(() => !queryPayload.workflowId, [queryPayload.workflowId])

    useEffect(() => {
        const rowEl = document.querySelector<HTMLDivElement>(`tr[data-row-key='${logId}']`)
        if (rowEl) {
            rowEl.style.background = 'var(--color-theme-3)'
        }
    }, [logId])

    const columnWidthMap = useMemo(() => {
        if (isAllWorkflow) {
            return {
                status: 140,
                runEnv: 140,
                envId: 140,
                executionTime: 200,
                operator: 200
            }
        }
        return {
            status: 110,
            runEnv: 100,
            envId: 100,
            executionTime: 180,
            operator: 180
        }
    }, [isAllWorkflow])

    const columns = useMemo<ColumnType<WorkflowRunLog>[]>(
        () => [
            {
                title: '运行状态',
                key: 'status',
                width: columnWidthMap['status'],
                formatter(value: FlowRunStatus) {
                    return (
                        <Flex gap={8} alignItems="center">
                            <IconFont size={24} color={RUNNING_STATUS_COLOR_MAP[value]} type={RUNNING_STATUS_ICON_MAP[value]} />
                            <Text>{RUNNING_STATUS_MAP[value]}</Text>
                        </Flex>
                    )
                }
            },
            {
                title: '运行类型',
                key: 'runEnv',
                width: columnWidthMap['runEnv'],
                formatter(value: RunType) {
                    return RUNNING_TYPE_MAP[value]
                }
            },
            {
                title: '数据环境',
                key: 'envId',
                width: columnWidthMap['envId'],
                formatter(_, record) {
                    const env = {
                        envId: record.envId,
                        envName: record.envName
                    }
                    return <EnvTag style={{ width: 'fit-content', paddingRight: 2 }} data={env} />
                }
            },
            {
                title: '运行时间',
                key: 'executionTime',
                width: columnWidthMap['executionTime'],
                formatter(value: number) {
                    const dateStr = lightFormat(value, 'yyyy/MM/dd HH:mm:ss')
                    return (
                        <TooltipText
                            title={dateStr}
                            render={ref => (
                                <Box style={{ overflow: 'hidden', textOverflow: 'ellipsis' }} ref={ref}>
                                    {dateStr}
                                </Box>
                            )}
                        />
                    )
                }
            },
            {
                title: '操作人',
                key: 'operator',
                align: 'right',
                width: columnWidthMap['operator'],
                formatter(_, record) {
                    return (
                        <TooltipText
                            title={record.operator}
                            render={ref => (
                                <Box style={{ overflow: 'hidden', textOverflow: 'ellipsis' }} ref={ref}>
                                    {record.operator}
                                </Box>
                            )}
                        />
                    )
                }
            }
        ],
        [columnWidthMap]
    )

    const finalColumns = useMemo<ColumnType<WorkflowRunLog>[]>(() => {
        if (!isAllWorkflow) {
            return columns
        }

        return [
            {
                title: '流程',
                key: 'name'
            },
            ...columns
        ]
    }, [columns, isAllWorkflow])

    const runningOfDataEnvOptions = useMemo(
        () => [
            { label: '全部', value: 'all' },
            ...envList.map(item => ({
                label: item.envName,
                value: item.envId
            }))
        ],
        [envList]
    )

    const filterContent = useMemo(
        () => (
            <Flex gap={28} mb={12} justifyContent="space-between" style={{ width: '100%' }}>
                <Flex alignItems="center" gap={12} style={{ width: 220 }}>
                    <Text color="var(--color-gray-500)" size={14}>
                        运行状态
                    </Text>
                    <Select
                        styles={{ root: { width: 140 } }}
                        options={FLOW_RUNNING_STATUS_OPTIONS}
                        value={queryPayload.status}
                        onChange={v => setQueryPayload(draft => void (draft.status = v as FlowRunStatus))}
                    />
                </Flex>
                <Flex alignItems="center" gap={12} style={{ width: 220 }}>
                    <Text color="var(--color-gray-500)" size={14}>
                        运行类型
                    </Text>
                    <Select
                        styles={{ root: { width: 140 } }}
                        options={RUNNING_TYPES_OPTIONS}
                        value={queryPayload.runEnv}
                        onChange={v => setQueryPayload(draft => void (draft.runEnv = v as RunType))}
                    />
                </Flex>
                <Flex alignItems="center" gap={12} style={{ width: 220 }}>
                    <Text color="var(--color-gray-500)" size={14}>
                        数据环境
                    </Text>
                    <Select
                        styles={{ root: { width: 140 } }}
                        options={runningOfDataEnvOptions}
                        value={queryPayload.envId}
                        onChange={v => setQueryPayload(draft => void (draft.envId = v))}
                    />
                </Flex>
            </Flex>
        ),
        [queryPayload.envId, queryPayload.runEnv, queryPayload.status, runningOfDataEnvOptions, setQueryPayload]
    )

    return (
        <Box px={24} py={16}>
            {isAllWorkflow ? (
                <SCxHeader justifyContent="space-between">
                    <Text size={20} weight={600}>
                        运行日志
                    </Text>
                    <Box style={{ width: 740 }}>{filterContent}</Box>
                </SCxHeader>
            ) : (
                filterContent
            )}
            <Table
                data={logs}
                columns={finalColumns}
                rowKey="workflowInstanceId"
                onRowClick={onRowClick}
                pagination={{ total, pageSize: 50, onChange: setCurrentPage }}
                styles={{
                    table: {
                        width: '100%'
                    },
                    tr: {
                        cursor: 'pointer',
                        '&:hover': {
                            backgroundColor: 'var(--color-gray-100)'
                        }
                    }
                }}
            />
        </Box>
    )
})

export const FlowHistoryModal = () => {
    const { id: workflowId, onFlowChange, log } = useFlowContainerContext()
    const rootEl = useStore(s => s.domNode)
    const { setModalState } = useFlowContainerContext()

    const handleRowClick = useCallback(
        (val: WorkflowRunLog) => {
            onFlowChange?.({
                mode: 'log',
                logId: val.workflowInstanceId
            })
        },
        [onFlowChange]
    )

    return (
        <DesktopModal
            open
            onClose={() => setModalState({})}
            title="运行记录"
            withOverlay={false}
            closeOnClickOverlay={false}
            trapFocus={false}
            width={742}
            target={rootEl}
            styles={{
                root: { pointerEvents: 'none' },
                modal: {
                    pointerEvents: 'initial',
                    maxWidth: '100%',
                    minWidth: 460,
                    position: 'absolute',
                    top: 60,
                    right: 12,
                    bottom: 12
                },
                body: {
                    padding: '0px !important'
                }
            }}
            transition={{ from: { x: '100%' }, to: { x: '0%' } }}
        >
            <FlowHistory workflowId={workflowId} logId={log?.instanceId} onRowClick={handleRowClick} />
        </DesktopModal>
    )
}
